(function() {
  'use strict';

  class ProductPriceIncrease {
    constructor(element) {
      this.element = element;
      this.sectionId = element.dataset.sectionId;
      this.unitPrice = parseInt(element.dataset.unitPrice) || 0;
      this.quantityInput = null;
      this.priceValueElement = element.querySelector('[data-price-increase-value]');
      this.discountValueElement = element.querySelector('[data-price-increase-discount-value]');
      this.discountMultiplier = 0.75; // 75% discount
      this.productSelector = null;
      
      this.init();
    }

    init() {
      // Find the quantity input within the same product form
      const productForm = document.querySelector(`#ProductForm-${this.sectionId}`);
      
      if (productForm) {
        this.quantityInput = productForm.querySelector('quantity-input input[name="quantity"]');
        
        // Listen for quantity changes
        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', () => this.updatePrice());
          this.quantityInput.addEventListener('input', () => this.updatePrice());
        }
      }

      // Find product-selector to listen for variant changes
      this.productSelector = document.querySelector(`product-selector[data-section-id="${this.sectionId}"]`);
      
      if (this.productSelector) {
        // Override renderProductInfo to also update price_increase
        const originalRenderProductInfo = this.productSelector.renderProductInfo.bind(this.productSelector);
        this.productSelector.renderProductInfo = () => {
          originalRenderProductInfo();
          // Wait for DOM to update, then update our price
          setTimeout(() => {
            this.updateUnitPriceFromDOM();
            this.updatePrice();
          }, 150);
        };

        // Also listen for variant changes
        this.productSelector.addEventListener('change', (event) => {
          if (event.target.type === 'number') return;
          setTimeout(() => {
            this.updateUnitPrice();
            this.updatePrice();
          }, 100);
        });
      }

      // Initial price calculation
      this.updatePrice();
    }

    updateUnitPrice() {
      // Get price from product-selector's current variant
      if (this.productSelector && this.productSelector.currentVariant) {
        this.unitPrice = this.productSelector.currentVariant.price || 0;
        this.element.dataset.unitPrice = this.unitPrice;
      }

      // Also check if the price_increase element's data-unit-price was updated
      const updatedUnitPrice = parseInt(this.element.dataset.unitPrice);
      if (updatedUnitPrice && updatedUnitPrice !== this.unitPrice) {
        this.unitPrice = updatedUnitPrice;
      }
    }

    updateUnitPriceFromDOM() {
      // After renderProductInfo updates the DOM, check if price_increase block was updated
      const updatedElement = document.querySelector(`[data-price-increase][data-section-id="${this.sectionId}"]`);
      if (updatedElement) {
        const newUnitPrice = parseInt(updatedElement.dataset.unitPrice);
        if (newUnitPrice && newUnitPrice !== this.unitPrice) {
          this.unitPrice = newUnitPrice;
          this.element.dataset.unitPrice = this.unitPrice;
        }
        // Also update the price value element if it was replaced
        const newPriceValueElement = updatedElement.querySelector('[data-price-increase-value]');
        if (newPriceValueElement && newPriceValueElement !== this.priceValueElement) {
          this.priceValueElement = newPriceValueElement;
        }
        // Also update the discount value element if it was replaced
        const newDiscountValueElement = updatedElement.querySelector('[data-price-increase-discount-value]');
        if (newDiscountValueElement && newDiscountValueElement !== this.discountValueElement) {
          this.discountValueElement = newDiscountValueElement;
        }
      }
    }

    updatePrice() {
      const quantity = parseInt(this.quantityInput?.value || 1);
      const isSingleQuantity = quantity <= 1;

      // When only one item is selected, show the normal price and hide discount styling
      if (isSingleQuantity) {
        if (this.discountValueElement) {
          this.discountValueElement.style.display = 'none';
        }
        if (this.priceValueElement) {
          this.priceValueElement.style.display = '';
          this.priceValueElement.style.textDecoration = 'none';
          this.updatePriceDisplay(this.unitPrice, this.priceValueElement);
        }
        return;
      }

      // Restore discount styling for quantities greater than one
      if (this.discountValueElement) {
        this.discountValueElement.style.display = '';
      }
      if (this.priceValueElement) {
        this.priceValueElement.style.display = '';
        this.priceValueElement.style.textDecoration = 'line-through';
      }

      const totalPrice = this.unitPrice * quantity;
      const totalDiscountPrice = totalPrice * this.discountMultiplier;

      // Format and update the regular price
      if (this.priceValueElement) {
        this.updatePriceDisplay(totalPrice, this.priceValueElement);
      }

      // Format and update the discount price
      if (this.discountValueElement) {
        this.updatePriceDisplay(totalDiscountPrice, this.discountValueElement);
      }
    }

    updatePriceDisplay(price, element) {
      if (!element) return;

      // Use formatPrice function from global.js if available
      if (typeof formatPrice === 'function' && window.theme?.settings?.money_with_currency_format) {
        const formattedPrice = formatPrice(
          window.theme.settings.money_with_currency_format,
          price / 100
        );
        element.textContent = formattedPrice;
      } else if (window.Shopify && window.Shopify.formatMoney) {
        element.textContent = window.Shopify.formatMoney(
          price,
          window.money_format || '{{amount}}'
        );
      } else {
        // Fallback: format manually
        const amount = (price / 100).toFixed(2);
        const currencySymbol = window.Shopify?.currency?.active || '€';
        element.textContent = `${currencySymbol}${amount}`;
      }
    }
  }

  // Initialize all price increase elements
  function initPriceIncrease() {
    const priceIncreaseElements = document.querySelectorAll('[data-price-increase]');
    priceIncreaseElements.forEach(element => {
      if (!element.priceIncreaseInstance) {
        element.priceIncreaseInstance = new ProductPriceIncrease(element);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPriceIncrease);
  } else {
    initPriceIncrease();
  }

  // Also handle dynamically added elements (e.g., after variant change updates DOM)
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) { // Element node
          // Check if the node itself is a price increase element
          if (node.matches && node.matches('[data-price-increase]') && !node.priceIncreaseInstance) {
            node.priceIncreaseInstance = new ProductPriceIncrease(node);
          }
          // Check for nested price increase elements
          const priceIncrease = node.querySelector?.('[data-price-increase]');
          if (priceIncrease && !priceIncrease.priceIncreaseInstance) {
            priceIncrease.priceIncreaseInstance = new ProductPriceIncrease(priceIncrease);
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
